// src/pages/GestionarReclamos.jsx
import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { useReclamos } from "../hooks/useReclamos";
import { ESTADOS_CONFIG, CATEGORIAS_CONFIG, PRIORIDADES_CONFIG } from "../constants";
import { formatText } from "../components/common/formatHelpers";
import apiService from "../services/api.service";
import { Select } from "../components/common/Select";
import { Input } from "../components/common/Input";
import { Button } from "../components/common/Button";
import { Badge } from "../components/common/Badge";
import { Card } from "../components/common/Card";
import { Alert } from "../components/common/Alert";
import { Loading } from "../components/common/Loading";

const PRIORIDAD_PESO = { alta: 3, media: 2, baja: 1 };

export default function GestionarReclamos() {
  const { hasAdminAccess } = useAuth();
  const { reclamos, loading, updateEstado, error, updatePrioridad } = useReclamos(true);
  const navigate = useNavigate();

  const [filters, setFilters] = useState({
    estado: "",
    categoria: "",
    prioridad: "",
    search: ""
  });
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");



  // Filtrar reclamos
  const filteredReclamos = useMemo(() => {
    // Primero filtramos los reclamos
    const filtered = reclamos.filter(reclamo => {
      const matchPrioridad = !filters.prioridad || reclamo.prioridad === filters.prioridad;
      const matchEstado = !filters.estado || reclamo.estado === filters.estado;
      const matchCategoria = !filters.categoria || reclamo.categoria === filters.categoria;
      const matchSearch = !filters.search ||
        reclamo.titulo.toLowerCase().includes(filters.search.toLowerCase()) ||
        reclamo.descripcion.toLowerCase().includes(filters.search.toLowerCase());
      return matchEstado && matchCategoria && matchPrioridad && matchSearch;
    });

    // Definir el orden de prioridad de estados
    const estadoPeso = {
      'pendiente': 3,
      'en_progreso': 2,
      'resuelto': 1
    };

    // Luego ordenamos los reclamos
    return filtered.sort((a, b) => {
      // Primero por estado según el peso definido
      const estadoA = estadoPeso[a.estado] || 0;
      const estadoB = estadoPeso[b.estado] || 0;
      if (estadoA !== estadoB) return estadoB - estadoA;

      // Si tienen el mismo estado, ordenar por prioridad
      const prioridadA = PRIORIDAD_PESO[a.prioridad] || 0;
      const prioridadB = PRIORIDAD_PESO[b.prioridad] || 0;
      if (prioridadA !== prioridadB) return prioridadB - prioridadA;

      // Si tienen la misma prioridad, ordenar por votos de "Existe"
      const aExisteCount = (a.feedbacks || []).filter(f => f.existe === true).length;
      const bExisteCount = (b.feedbacks || []).filter(f => f.existe === true).length;
      if (aExisteCount !== bExisteCount) {
        return bExisteCount - aExisteCount;
      }

      // Si tienen la misma cantidad de votos, ordenar por fecha (más recientes primero)
      return new Date(b.fecha_creacion) - new Date(a.fecha_creacion);
    });
  }, [reclamos, filters]);

  const handleEstadoChange = async (reclamoId, nuevoEstado, estadoActual) => {
    try {
      await updateEstado(reclamoId, nuevoEstado);

      // Crear entrada en el historial
      await apiService.createHistorial({
        reclamo_id: parseInt(reclamoId),
        comentario: `Cambio de estado de ${formatText(estadoActual)} a ${formatText(nuevoEstado)}`
      });

      setSuccessMessage("Estado actualizado correctamente");
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (err) {
      setErrorMessage(err.message || "Error al actualizar el estado");
    }
  };

  const handlePrioridadChange = async (reclamoId, nuevaPrioridad, prioridadActual) => {
    try {
      await updatePrioridad(reclamoId, nuevaPrioridad);

      // Crear entrada en el historial
      await apiService.createHistorial({
        reclamo_id: parseInt(reclamoId),
        comentario: `Cambio de prioridad de ${formatText(prioridadActual)} a ${formatText(nuevaPrioridad)}`
      });

      setSuccessMessage("Prioridad actualizada correctamente");
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (err) {
      setErrorMessage(err.message || "Error al actualizar la prioridad");
    }
  };

  const getEstadoVariant = (estado) => {
    switch (estado) {
      case "resuelto": return "success";
      case "en_progreso": return "info";
      case "pendiente": return "warning";
      default: return "default";
    }
  };

  const getPrioridadVariant = (prioridad) => {
    switch (prioridad) {
      case "alta": return "danger";   // rojo
      case "media": return "info";    // azul
      case "baja": return "success";  // verde
      default: return "default";
    }
  };

  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleDateString("es-AR", {
        year: "numeric",
        month: "short",
        day: "numeric"
      });
    } catch {
      return "N/A";
    }
  };

  if (!hasAdminAccess) {
    return (
      <Alert variant="error">
        No tienes permisos para acceder a esta sección
      </Alert>
    );
  }

  if (loading) {
    return <Loading text="Cargando reclamos..." />;
  }

  return (
    <div style={{ maxWidth: "1400px", margin: "0 auto" }}>
      {/* Header */}
      <div style={{ marginBottom: "32px" }}>
        <h1 style={{
          fontSize: "32px",
          fontWeight: "700",
          color: "#1e293b",
          margin: "0 0 8px 0"
        }}>
          Gestionar Reclamos
        </h1>
        <p style={{
          fontSize: "16px",
          color: "#64748b",
          margin: 0
        }}>
          Total: {filteredReclamos.length} reclamo{filteredReclamos.length !== 1 ? 's' : ''}
        </p>
      </div>

      {/* Mensajes */}
      {successMessage && (
        <Alert variant="success" onClose={() => setSuccessMessage("")}>
          {successMessage}
        </Alert>
      )}

      {(errorMessage || error) && (
        <Alert variant="error" onClose={() => setErrorMessage("")}>
          {errorMessage || error}
        </Alert>
      )}

      {/* Filtros */}
      <Card padding="24px" style={{ marginBottom: "24px" }}>
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
          gap: "16px"
        }}>
          <Select
            label="Estado"
            value={filters.estado}
            onChange={(e) => setFilters({ ...filters, estado: e.target.value })}
            options={[
              { value: "", label: "Todos los estados" },
              ...ESTADOS_CONFIG.map(e => ({ value: e.value, label: e.label }))
            ]}
          />

          <Select
            label="Categoría"
            value={filters.categoria}
            onChange={(e) => setFilters({ ...filters, categoria: e.target.value })}
            options={[
              { value: "", label: "Todas las categorías" },
              ...CATEGORIAS_CONFIG.map(c => ({ value: c.value, label: c.label }))
            ]}
          />

          <Select
            label="Prioridad"
            value={filters.prioridad}
            onChange={(e) => setFilters({ ...filters, prioridad: e.target.value })}
            options={[
              { value: "", label: "Todas las prioridades" },
              ...PRIORIDADES_CONFIG.map(p => ({ value: p.value, label: p.label }))
            ]}
          />


          <Input
            label="Buscar"
            placeholder="Título o descripción..."
            icon="🔍"
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
          />
        </div>
      </Card>

      {/* Estadísticas rápidas */}
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
        gap: "16px",
        marginBottom: "24px"
      }}>
        {ESTADOS_CONFIG.map(estado => {
          const count = reclamos.filter(r => r.estado === estado.value).length;
          return (
            <Card key={estado.value} padding="16px">
              <div style={{ textAlign: "center" }}>
                <div style={{
                  fontSize: "28px",
                  fontWeight: "700",
                  color: estado.color,
                  marginBottom: "4px"
                }}>
                  {count}
                </div>
                <div style={{ fontSize: "13px", color: "#64748b" }}>
                  {estado.label}
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {/* Lista de reclamos */}
      {filteredReclamos.length === 0 ? (
        <Card padding="60px 40px">
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: "64px", marginBottom: "16px" }}>🔍</div>
            <h2 style={{
              fontSize: "24px",
              fontWeight: "600",
              color: "#1e293b",
              marginBottom: "12px"
            }}>
              No se encontraron reclamos
            </h2>
            <p style={{
              fontSize: "16px",
              color: "#64748b"
            }}>
              Intenta ajustar los filtros de búsqueda
            </p>
          </div>
        </Card>
      ) : (
        <div style={{
          display: "grid",
          gap: "16px"
        }}>
          {filteredReclamos.map((reclamo) => {

            const categoria = CATEGORIAS_CONFIG.find(c => c.value === reclamo.categoria);
            const estadoActual = ESTADOS_CONFIG.find(e => e.value === reclamo.estado);

            return (
              <Card
                key={reclamo.id}
                padding="24px"
                hover
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "16px"
                }}
              >
                {/* IZQUIERDA: info general */}
                <div style={{ flex: 2 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "6px" }}>
                    <h3 style={{ fontSize: "18px", fontWeight: "600", color: "#1e293b", margin: 0 }}>
                      {reclamo.titulo}
                    </h3>
                    <Badge variant={getEstadoVariant(reclamo.estado)}>
                      {estadoActual?.label || reclamo.estado}
                    </Badge>
                    <Badge variant={getPrioridadVariant(reclamo.prioridad)}>
                      {PRIORIDADES_CONFIG.find(p => p.value === reclamo.prioridad)?.label || reclamo.prioridad}
                    </Badge>
                  </div>
                  <p style={{
                    fontSize: "15px",
                    color: "#64748b",
                    margin: "0 0 16px 0",
                    lineHeight: "1.5"
                  }}>
                    {reclamo.descripcion}
                  </p>

                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: "16px",
                      marginBottom: "8px"
                    }}
                  >
                    {reclamo.confiabilidad_calculada != null && (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                          backgroundColor: "#f1f5f9",
                          padding: "4px 8px",
                          borderRadius: "6px",
                        }}
                      >
                        <span>📈</span>
                        <span style={{ fontSize: "14px", color: "#334155" }}>
                          Confiabilidad:{" "}
                          <strong>
                            {reclamo.confiabilidad_calculada.toFixed(2)}%
                          </strong>
                        </span>
                      </div>
                    )}


                    {reclamo.promedio_calificacion > 0 && (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                          backgroundColor: "#fef9c3",
                          padding: "4px 8px",
                          borderRadius: "6px",
                        }}
                      >
                        <span>⭐</span>
                        <span style={{ fontSize: "14px", color: "#854d0e" }}>
                          Calidad de solución:{" "}
                          <strong>{reclamo.promedio_calificacion.toFixed(1)} / 5</strong>
                        </span>
                      </div>
                    )}
                  </div>

                  <div style={{
                    display: "flex",
                    gap: "24px",
                    flexWrap: "wrap",
                    fontSize: "14px",
                    color: "#64748b"
                  }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                      <span>{categoria?.icon || "📌"}</span>
                      <span>{categoria?.label || reclamo.categoria}</span>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                      <span>📅</span>
                      <span>{formatDate(reclamo.fecha_creacion)}</span>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                      <span>📍</span>
                      <span style={{ fontFamily: "monospace", fontSize: "13px" }}>
                        {reclamo.latitud?.toFixed(4)}, {reclamo.longitud?.toFixed(4)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* DERECHA: acciones inline */}
                <div
                  style={{
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                  }}
                >
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                    <label style={{ fontSize: "14px", fontWeight: "600", color: "#334155", marginBottom: "6px" }}>
                      Estado
                    </label>
                    <Select
                      value={reclamo.estado}
                      onChange={(e) => handleEstadoChange(reclamo.id, e.target.value, reclamo.estado)}
                      options={ESTADOS_CONFIG.map((e) => ({ value: e.value, label: e.label }))}
                      style={{
                        width: "130px",
                        padding: "8px 12px",
                        fontSize: "15px",
                        border: "2px solid #e2e8f0",
                        borderRadius: "8px",
                        outline: "none",
                        backgroundColor: "white",
                        cursor: "pointer",
                        fontWeight: "500",
                        color: "#334155",
                        transition: "all 0.2s ease"
                      }}
                    />
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                    <label style={{ fontSize: "14px", fontWeight: "600", color: "#334155", marginBottom: "6px" }}>
                      Prioridad
                    </label>
                    <Select
                      value={reclamo.prioridad}
                      onChange={(e) => handlePrioridadChange(reclamo.id, e.target.value, reclamo.prioridad)}
                      options={PRIORIDADES_CONFIG.map((p) => ({ value: p.value, label: p.label }))}
                      style={{
                        width: "100%",
                        padding: "8px 12px",
                        fontSize: "15px",
                        border: "2px solid #e2e8f0",
                        borderRadius: "8px",
                        outline: "none",
                        backgroundColor: "white",
                        cursor: "pointer",
                        fontWeight: "500",
                        color: "#334155",
                        transition: "all 0.2s ease"
                      }}
                    />
                  </div>
                  <Button
                    variant="primary"
                    onClick={() => navigate(`/reclamo/${reclamo.id}`)}
                  >
                    Ver
                  </Button>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}